<template>
    <div class="container-fluid">
        <PageBanner
            title="ASA"
            subtext="Review ASA system data and case closure reporting from one place."
        />

        <q-tabs v-model="tab" dense class="tabs-container" active-color="primary">
            <q-tab no-caps class="client-graph-tab" :name="asaTabName" label="ASA System Data" />
            <q-tab no-caps class="client-graph-tab" :name="closureTabName" label="Case Closure" />
        </q-tabs>

        <div class="q-tab-panel">
            <FilterComponent v-model="activeFilters" />

        </div>
        
        <q-tab-panels v-model="tab">
            <q-tab-panel :name="asaTabName">
                <div class="container-fluid">
                    <AsaDataComponent ref="asaComponent" :data="filteredSystemData" :charts="systemCharts" :loading="systemDataLoading" />
                </div>
            </q-tab-panel>

            <q-tab-panel :name="closureTabName">
                <div class="container-fluid" style="display: flex; flex-direction: column;">
                    <q-btn v-if="false" icon="refresh" color="green" style="margin-left: auto;" @click="updateClosureFormData()" :loading="updatingFormData" />
                    <CaseClosureComponent ref="closureComponent" :data="closureData" :charts="closureCharts" :loading="closureDataLoading" />
                </div>
            </q-tab-panel>
        </q-tab-panels>
    </div>
</template>

<script>
import { ref } from 'vue'
import api from "../../services/api/api";
import notify from '@/services/util/notify';

import CaseClosureComponent from "./CaseClosureComponent.vue";
import AsaDataComponent from "./AsaDataComponent.vue";
import FilterComponent from './FilterComponent.vue';
import PageBanner from '../ClientComponents/DashboardComponents/PageBanner.vue';

import { isEqual } from "lodash";

export default ({
    name: "Asa",
    components: {  AsaDataComponent, CaseClosureComponent, FilterComponent, PageBanner },

    data() {
        return {
            tab: ref('asa'),
            slide: ref('style'),

            asaTabName: "asa",
            closureTabName: "caseClosure",

            closureData: [],
            closureDataLoading: false,
            closureCharts: [],

            systemData: [],
            systemDataLoading: false,
            systemCharts: [],

            filters: {},

            updatingFormData: false,
        }
    },

    computed: {
        activeFilters: {
            get() {
                return this.filters[this.tab];
            },
            set(value) {
                this.filters[this.tab] = value;
            },
        },

        uniqueSystemValues() {
            const uniqueValues = {};
            const columns = Object.keys(this.systemData[0] ?? []);

            for (const column of columns) {
                uniqueValues[column] = [...new Set(this.systemData.map((row) => row[column]))];
            }

            return uniqueValues;
        },

        uniqueClosureValues() {
            const uniqueValues = {};
            const columns = Object.keys(this.closureData[0] ?? []);

            for (const column of columns) {
                uniqueValues[column] = [...new Set(this.closureData.map((row) => row[column]))];
            }

            return uniqueValues;
        },

        filteredSystemData() {
            return this.filterRows(this.systemData, this.activeFilters);
        },

        filteredClosureData() {
            return this.filterRows(this.closureData, this.activeFilters);
        },

        activeFiltersString() {
            return JSON.stringify(this.activeFilters);
        }
    },
    
    created() {
        this.initData();
        this.initFilters();
    },

    watch: {
        activeFiltersString: {
            handler(newVal, oldVal) {
                if (!newVal || !oldVal) {
                    return;
                }

                const newFilters = Object.values(JSON.parse(newVal ?? "{}"));
                const oldFilters = Object.values(JSON.parse(oldVal ?? "{}"));
                const newFilterValues = this.extractUniqueValuesFromFilters(newFilters);
                const oldFilterValues = this.extractUniqueValuesFromFilters(oldFilters);
                
                if (isEqual(newFilterValues, oldFilterValues)) {
                    return;
                }

                const filterParams = this.getChartFilters();

                switch (this.tab) {
                    case this.asaTabName:
                        this.retrieveASADemographicData(filterParams);
                        break;
                    case this.closureTabName:
                        break;

                }
            },
            deep: true,
        },
    },
    
    methods: {
        filterRows(rows, filters) {
            return rows.filter(row => {
                return Object.keys(filters).every(filterKey => {
                    if (filters[filterKey].value.length === 0) {
                        return true;
                    }
                    return filters[filterKey].value.includes(row[filterKey]);
                });
            });
        },
        
        async initData() {
            await Promise.all([
                this.retrieveASATimelineData(),
                this.retrieveASADemographicData(),
                this.retrieveCaseClosureData(),
                this.retrieveCaseClosureGraph(),
            ]);
        },

        async updateClosureFormData() {
            this.updatingFormData = true;

            try {
                await api.forms.updateCaseClosureFormData();
                await Promise.all([this.retrieveCaseClosureData(), this.retrieveCaseClosureGraph()]);
            } catch {
                notify.error("There was an issue refreshing the data. Please try again later.", "top");
            } finally {
                this.updatingFormData = false;
            }
        },

        initFilters() {
            this.filters = {
                [this.asaTabName]: {
                    type: { value: [], label: "Case Type", options: [] },
                    referrer_school: { value: [], label: "School", options: [] },
                    ethnicity: { value: [], label: "Ethnicity", options: [] },
                    gender: { value: [], label: "Gender", options: [] },
                    iwi: { value: [], label: "Iwi", options: [] },
                    provider_service_area: { value: [], label: "Service Area", options: [] }
                },
                [this.closureTabName]: {

                }
            };
        },

        async retrieveCaseClosureData() {
            this.closureDataLoading = true;
            api.asa.getASACaseClosureData().then((res) => {
                this.closureData = res.data
                this.updateFiltersForClosureData();
            }).catch((error) => {
                if (error?.response?.data) {
                    notify.error(error.response.data, 'top');
                } else {
                    notify.error(error.message, 'top');
                }
            }).finally(() => {
                this.closureDataLoading = false;
            })
        },

        async retrieveCaseClosureGraph() {
            api.asa.getASACaseClosureGraph().then((res) => {
                this.closureCharts = res.data;
            }).catch((error) => {
                notify.error(error.message, "top");
            })
        },

        async retrieveASADemographicData(filterParams = {}) {
            api.asa.getASADemographicsGraph(filterParams).then((res) => {
                this.systemCharts = res.data;
            }).catch((error) => {
                notify.error(error.message, 'top');
            }).finally(() => {
                
            })
        },

        async retrieveASATimelineData(filterParams = {}) {
            this.systemDataLoading = true;
            api.asa.getASACaseTimelineData(filterParams).then((res) => {
                this.systemData = res.data;
                this.updateFiltersForSystemData();
            }).catch((error) => {
                if (error?.response?.data) {
                    notify.error(error.response.data, 'top');
                } else {
                    notify.error(error.message, 'top');
                }
            }).finally(() => {
                this.systemDataLoading = false
            })
        },

       updateFiltersForSystemData() {
            const keys = Object.keys(this.uniqueSystemValues);
            keys.forEach(key => {
                if (this.filters[this.asaTabName][key]) {
                    this.filters[this.asaTabName][key].options = this.uniqueSystemValues[key];
                }
            });
        },

        updateFiltersForClosureData() {
            
        },

        getChartFilters() {
            return Object.entries(this.activeFilters).reduce((acc, [column, value]) => {
                acc[column] = value.value;
                return acc;
            }, {});
        },

        extractUniqueValuesFromFilters(...filters) {
            const results = new Set();

            const extract = (obj) => {
                if (Array.isArray(obj)) {
                    obj.forEach(item => results.add(item.value)); // Add each item in the array
                } else if (typeof obj === "object" && obj !== null) {
                    Object.values(obj).forEach(value => {
                        // Recursively extract values from nested objects
                        extract(value);
                    });
                }
            };

            // Iterate over each filter object
            filters.forEach(filter => extract(filter));
            return [...results].flat(); // Return the unique values as an array
        }
    }
})

</script>

<style>
@import '../../assets/styles/clientDemographics.css';
</style>
